import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Search, Bell, LogOut, Settings, User, UserCog, HelpCircle, Command, CheckCircle2 } from 'lucide-react';
import { InputGroup, InputGroupAddon, InputGroupInput } from '../InputGroup';
import { Badge } from '../Badge';
import { Avatar, AvatarFallback, AvatarImage } from '../Avatar';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../DropdownMenu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../Dialog';
import { Button } from '../Button';
import { SidebarTrigger } from '../Sidebar';
import { NotificationsSheet } from './NotificationsSheet';
import { CommandSearch } from './CommandSearch';
import { mockApi } from '../../lib/mockApi';
import { ROLE_OPTIONS, clearSession, getRoleAvatarUrl, getSession, setSession, type SessionRole } from '../../lib/auth';
export function TopHeader() {
  const [session, setSessionState] = useState<SessionRole>(() => getSession());
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const nav = useNavigate();
  useEffect(() => {
    const loadNotifs = async () => {
      const data = await mockApi.getNotifications();
      setUnreadCount(data.filter(n => !n.read).length);
    };
    loadNotifs();
    const unsub = mockApi.subscribe(loadNotifs);
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    const onSessionChange = (e: any) => {
      if (e.detail) setSessionState(e.detail as SessionRole);else setSessionState(getSession());
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('gs_session_change', onSessionChange as EventListener);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('gs_session_change', onSessionChange as EventListener);
    };
  }, []);
  const switchRole = (roleId: string) => {
    const r = ROLE_OPTIONS.find(x => x.roleId === roleId);
    if (!r) return;
    const next = setSession(r);
    setSessionState(next);
    setRoleOpen(false);
    toast.success(`Peran berubah: ${r.role}`, {
      description: `Sebagai ${r.code} — tampilan akses disesuaikan.`
    });
  };
  const logout = () => {
    clearSession();
    toast.success('Sesi diakhiri secara aman', {
      description: 'Mengarahkan ke layar otentikasi...'
    });
    setTimeout(() => nav('/login', {
      replace: true
    }), 400);
  };
  const avatarUrl = getRoleAvatarUrl(session.roleId);
  return <>
      <header className="flex h-14 sm:h-16 items-center justify-between border-b border-border/60 bg-background/95 px-3 sm:px-5 backdrop-blur supports-[backdrop-filter]:bg-background/70 sticky top-0 z-30" data-id="element-900">
        {/* Left */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0" data-id="element-901">
          <SidebarTrigger className="md:hidden -ml-1" data-id="element-902" />

          <Link to="/dashboard" className="flex items-center gap-2 sm:gap-2.5 hover:opacity-90 lg:hidden" data-id="element-903">
            <div className="flex size-8 sm:size-9 items-center justify-center rounded-lg bg-primary/10 border border-primary/20 text-primary shrink-0" data-id="element-904">
              <Shield className="size-4 sm:size-4.5" strokeWidth={2.4} data-id="element-905" />
            </div>
            <div className="flex flex-col min-w-0" data-id="element-906">
              <h1 className="font-heading text-sm sm:text-base font-bold tracking-tight text-foreground leading-none" data-id="element-907">
                Garuda Shield
              </h1>
              <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground hidden sm:block mt-0.5" data-id="element-908">
                Cyber Situation Room
              </span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-2" data-id="element-909">
            <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 font-mono text-[10px] gap-1.5" data-id="element-910">
              <span className="size-1.5 rounded-full bg-emerald-500" data-id="element-911" />
              PRODUKSI
            </Badge>
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground" data-id="element-912">
              v4.1.0
            </span>
          </div>
        </div>

        {/* Center: Search */}
        <div className="hidden md:flex flex-1 max-w-xl items-center justify-center px-6" data-id="element-913">
          <button onClick={() => setSearchOpen(true)} className="w-full group" aria-label="Cari" data-id="element-914">
            <InputGroup className="w-full bg-muted/40 border-border/60 hover:border-primary/50 hover:bg-muted/60 transition pointer-events-none" data-id="element-915">
              <InputGroupAddon data-id="element-916">
                <Search className="size-4 text-muted-foreground group-hover:text-foreground transition" data-id="element-917" />
              </InputGroupAddon>
              <InputGroupInput readOnly placeholder="Cari isu, aktor, wilayah, atau perintah cepat..." className="border-0 bg-transparent placeholder:text-muted-foreground/70 cursor-pointer" data-id="element-918" />
              <InputGroupAddon align="inline-end" data-id="element-919">
                <kbd className="font-mono text-[9px] text-muted-foreground bg-background border border-border/60 px-1.5 py-0.5 rounded" data-id="element-920">
                  ⌘K
                </kbd>
              </InputGroupAddon>
            </InputGroup>
          </button>
        </div>

        {/* Right */}
        <div className="flex items-center gap-1 sm:gap-2" data-id="element-921">
          <button className="md:hidden flex items-center justify-center size-9 rounded-md hover:bg-muted transition" onClick={() => setSearchOpen(true)} aria-label="Cari" data-id="element-922">
            <Search className="size-5 text-muted-foreground" data-id="element-923" />
          </button>

          <button onClick={() => nav('/help')} className="hidden lg:flex items-center justify-center size-9 rounded-md hover:bg-muted transition" aria-label="Bantuan" title="Bantuan" data-id="element-924">
            <HelpCircle className="size-5 text-muted-foreground" data-id="element-925" />
          </button>

          <button onClick={() => setNotifOpen(true)} className="relative flex items-center justify-center size-9 rounded-md hover:bg-muted transition" aria-label="Notifikasi" data-id="element-926">
            <Bell className="size-5 text-muted-foreground" data-id="element-927" />
            {unreadCount > 0 && <span className="absolute top-1.5 right-1.5 flex size-2 items-center justify-center" data-id="element-928">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" data-id="element-929"></span>
                <span className="relative inline-flex size-2 rounded-full bg-red-600" data-id="element-930"></span>
              </span>}
          </button>

          <DropdownMenu data-id="element-931">
            <DropdownMenuTrigger className="outline-none ml-1" data-id="element-932">
              <div className="flex items-center gap-2 rounded-full border border-border/60 bg-card hover:bg-muted transition p-0.5 pr-0 sm:pr-3 cursor-pointer" data-id="element-933">
                <Avatar className="size-7 sm:size-8" data-id="element-934">
                  <AvatarImage src={avatarUrl} data-id="element-935" />
                  <AvatarFallback className="bg-primary/10 text-primary text-[11px] font-bold" data-id="element-936">
                    {session.initials}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden sm:flex flex-col text-left" data-id="element-937">
                  <span className="text-xs font-bold leading-tight max-w-[140px] truncate" data-id="element-938">
                    {session.role}
                  </span>
                  <span className="font-mono text-[9px] text-muted-foreground leading-tight mt-0.5" data-id="element-939">
                    {session.code}
                  </span>
                </div>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 border-border/60 bg-card shadow-lg" data-id="element-940">
              <div className="px-2 py-2 flex items-center gap-2.5" data-id="element-941">
                <Avatar className="size-9" data-id="element-942">
                  <AvatarImage src={avatarUrl} data-id="element-943" />
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold" data-id="element-944">
                    {session.initials}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0" data-id="element-945">
                  <p className="text-xs font-bold leading-tight truncate" data-id="element-946">
                    {session.role}
                  </p>
                  <p className="font-mono text-[9px] text-muted-foreground truncate" data-id="element-947">
                    {session.email}
                  </p>
                </div>
              </div>
              <DropdownMenuSeparator className="bg-border/60" data-id="element-948" />
              <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => nav('/profile')} data-id="element-949">
                <User className="size-4 mr-2" data-id="element-950" /> Profil Pengguna
              </DropdownMenuItem>
              <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => setRoleOpen(true)} data-id="element-951">
                <UserCog className="size-4 mr-2" data-id="element-952" /> Ubah Peran
              </DropdownMenuItem>
              <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => setSearchOpen(true)} data-id="element-953">
                <Command className="size-4 mr-2" data-id="element-954" /> Command Palette
                <kbd className="ml-auto font-mono text-[9px] text-muted-foreground bg-muted px-1 py-0.5 rounded" data-id="element-955">
                  ⌘K
                </kbd>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-border/60" data-id="element-956" />
              <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => nav('/help')} data-id="element-957">
                <HelpCircle className="size-4 mr-2" data-id="element-958" /> Bantuan & FAQ
              </DropdownMenuItem>
              <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => nav('/settings')} data-id="element-959">
                <Settings className="size-4 mr-2" data-id="element-960" /> Pengaturan Sistem
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-border/60" data-id="element-961" />
              <DropdownMenuItem className="text-xs text-red-600 focus:text-red-600 cursor-pointer" onClick={logout} data-id="element-962">
                <LogOut className="size-4 mr-2" data-id="element-963" /> Keluar Aman
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <NotificationsSheet open={notifOpen} onOpenChange={setNotifOpen} data-id="element-964" />
      <CommandSearch open={searchOpen} onOpenChange={setSearchOpen} data-id="element-965" />

      <Dialog open={roleOpen} onOpenChange={setRoleOpen} data-id="element-966">
        <DialogContent className="max-w-lg" data-id="element-967">
          <DialogHeader data-id="element-968">
            <DialogTitle data-id="element-969">Ubah Peran Pengguna</DialogTitle>
            <DialogDescription data-id="element-970">
              Pilih peran untuk simulasi tingkat akses sesuai struktur RBAC.
              Tampilan modul akan menyesuaikan otomatis.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 my-2 max-h-[420px] overflow-y-auto pr-1" data-id="element-971">
            {ROLE_OPTIONS.map(r => {
            const active = session.roleId === r.roleId;
            return <button key={r.roleId} onClick={() => switchRole(r.roleId)} className={`p-3 rounded-lg border text-left transition ${active ? 'bg-primary/10 border-primary/50 ring-1 ring-primary/30' : 'bg-muted/30 border-border/60 hover:bg-muted/60 hover:border-border'}`} data-id="element-972">
                  <div className="flex items-start justify-between gap-2" data-id="element-973">
                    <div className="min-w-0 flex-1" data-id="element-974">
                      <p className="text-xs font-bold leading-tight" data-id="element-975">
                        {r.role}
                      </p>
                      <p className="font-mono text-[9px] text-muted-foreground mt-0.5" data-id="element-976">
                        {r.code} · {r.username}
                      </p>
                    </div>
                    {active && <CheckCircle2 className="size-4 text-primary shrink-0" data-id="element-977" />}
                  </div>
                </button>;
          })}
          </div>
          <DialogFooter data-id="element-978">
            <Button variant="outline" onClick={() => setRoleOpen(false)} data-id="element-979">
              Tutup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>;
}